/** Strings of the accounting VAT, contacts and recurring-invoices screens: `accVatContactsEn` is the source, `accVatContactsCs` must have the same keys. */
export const accVatContactsEn = {
  accVat: {
    title: "VAT",
    subtitle:
      "Supplies fall into a period by their date of taxable supply, not by the date of issue.",
    year: "Year",
    quarter: "Quarter",
    month: "Month",
    monthly: "Monthly",
    quarterly: "Quarterly",
    vatDue: "VAT due",
    vatRefund: "VAT refund",
    balance: "Output VAT {{output}} − input VAT {{input}}",
    building: "Building…",
    rebuild: "Rebuild",
    buildReturn: "Build the return",
    importing: "Importing…",
    importFiled: "Import filed returns",
    reasonsFoundOn: "Reasons found on",
    reasons: "Reasons for the additional return",
    reasonsHint:
      "Required when the additional return lowers the tax (§ 141 of the Tax Procedure Code): without them the return can't be sent by data box. They go in as a text attachment.",
    withFollowUp: "with a follow-up control statement",
    buildAdditional: "Build an additional return",
    buildError: "Couldn't build the return.",
    importError: "Couldn't import the filed returns.",
    markError: "Couldn't mark it.",
    footnote:
      "The files are uploaded to the tax portal (EPO). Hub prepares them, a person files them. They follow the portal's published XSD. An additional return carries only the differences against the last known tax; a follow-up control statement is complete.",
    output: "Output",
    input: "Input",
    form: {
      regular: "regular (B)",
      corrective: "corrective (O)",
      additional: "additional (D)",
    },
    mode: {
      standard: "domestic",
      reverse_charge: "reverse charge",
      eu_supply: "EU supply",
      export: "export",
      exempt: "exempt",
      outside_scope: "outside the scope",
    },
    messageState: {
      sent: "sent",
      inBox: "in the office's box",
      deliveredFiction: "delivered (fiction)",
      delivered: "delivered",
      unknown: "state {{state}}",
    },
    dataMessage: "data message",
    returns: {
      form: "Form",
      state: "State",
      taxDue: "Tax due",
      difference: "Difference",
      files: "Files",
      filed: "filed",
      portal: "(portal)",
      draft: "draft",
      markFiled: "Mark as filed",
      sendByDataBox: "Send by data box: {{label}}",
    },
    yearDocs: {
      title: "{{year}} from the documents",
      description:
        "The return as the documents make it, month by month: imported ones classified by the rules, foreign currency in crowns at the document's rate.",
      computing: "Computing…",
      month: "Month",
      outputVat: "Output VAT",
      deduction: "Deduction",
      taxDue: "Tax due",
      documents: "Documents",
      needsPerson: "Needs a person",
      toDecide: "{{count}} to decide",
    },
    lines: {
      title: "{{month}}/{{year}}: the return's lines from the documents",
      description:
        "Output {{output}} · deduction {{deduction}} · tax due {{taxDue}} (whole crowns, per line)",
      lineNo: "line {{key}}",
      base: "base {{value}}",
      vat: "VAT {{value}}",
      vatCzk: "VAT {{value}} CZK",
      notDeductible: "Not deductible, one-stop-shop tax ({{count}})",
      unplaced: "In no line until someone decides ({{count}})",
      label: {
        "1": "Domestic supplies, standard rate",
        "2": "Domestic supplies, reduced rate",
        "3": "Goods from another member state, standard",
        "4": "Goods from another member state, reduced",
        "5": "Services from another member state (§9(1)), standard",
        "6": "Services from another member state, reduced",
        "10": "Domestic reverse charge received (§92a), standard",
        "11": "Domestic reverse charge received, reduced",
        "12": "Other supplies taxed on receipt (§108), standard",
        "13": "Other supplies taxed on receipt, reduced",
        "21": "Services to another member state",
        "25": "Domestic reverse charge supplied (§92a)",
        "26": "Other supplies with the right to deduct",
        "40": "Deduction, from payers, standard rate",
        "41": "Deduction, from payers, reduced rate",
        "43": "Deduction of tax on lines 3 to 13, standard",
        "44": "Deduction of tax on lines 3 to 13, reduced",
      },
    },
    table: {
      rate: "Rate",
      mode: "Mode",
      base: "Base",
      vat: "VAT",
      nothing: "Nothing in this period.",
      total: "VAT total",
    },
  },
  accContacts: {
    role: {
      customer: "customer",
      supplier: "supplier",
      both: "customer + supplier",
    },
    title: "Contacts",
    subtitle:
      "Everybody we invoice or are invoiced by. A Hub customer bills through the contact it is linked to — the contact carries the identity that lands on the document.",
    close: "Close",
    newContact: "New contact",
    all: "All",
    searchPlaceholder: "Name, registration or VAT number…",
    empty:
      "Nothing here yet — the Fakturoid import fills the address book, or add a contact by hand.",
    col: {
      name: "Name",
      role: "Role",
      registration: "Registration",
      vatNo: "VAT no.",
      linkedCustomers: "Linked customers",
      documents: "Documents",
    },
    profile: "Profile",
    createError: "Couldn't create the contact.",
    newTitle: "New contact",
    newDescription: "A registration number is checked against the book — one company, one row.",
    ares: {
      hint: "Type a registration no. or start typing the name; the details come from ARES.",
      loading: "Looking up ARES…",
      notFound: "ARES has no company with this registration no.",
    },
    creating: "Creating…",
    create: "Create",
    field: {
      name: "Name",
      role: "Role",
      registrationNo: "Registration no.",
      vatNo: "VAT no.",
      email: "Email",
      phone: "Phone",
      street: "Street",
      zip: "ZIP",
      city: "City",
      country: "Country",
      dueDays: "Due in (days)",
      dueDaysPlaceholder: "Default from settings",
      currency: "Currency",
      note: "Note",
      active: "Active",
    },
    detail: {
      back: "Contacts",
      inactive: "inactive",
      noAddress: "No address on file",
      edit: "Edit",
      close: "Close",
      theyOweUs: "They owe us ({{count}})",
      weOweThem: "We owe them ({{count}})",
      overdue: "Of that overdue ({{count}})",
      identityTitle: "Billing identity",
      identityDescription: "What lands on the next document we issue them.",
      dueIn: "Due in",
      defaultFromSettings: "Default from settings",
      days_one: "{{count}} day",
      days_few: "{{count}} days",
      days_other: "{{count}} days",
      customersTitle: "Linked Hub customers",
      customersDescription: "Who bills through this contact.",
      supplierTitle: "As a supplier",
      supplierDescription: "Supplier records tied to this contact.",
      noSupplier: "None — nothing they invoiced us has been tied to a supplier record.",
      weInvoiced: "We invoiced them",
      theyInvoiced: "They invoiced us",
      lastTwenty: "The last twenty.",
      allLink: "All",
      nothingYet: "Nothing yet.",
      noCustomers: "None — this contact is billed directly, without a project side.",
      unlink: "unlink",
      linkCustomer: "Link a customer…",
      linkError: "Couldn't link the customer.",
      unlinkError: "Couldn't unlink the customer.",
      saved: "Contact saved",
      saveError: "Couldn't save the contact.",
      editTitle: "Edit contact",
      editDescription:
        "Documents already issued keep the identity they were issued with — this changes what the next one says.",
      saving: "Saving…",
      save: "Save",
    },
    status: {
      paid: "paid",
      partially_paid: "partially paid",
      issued: "issued",
      draft: "draft",
      cancelled: "cancelled",
    },
  },
  accRecurring: {
    title: "Recurring",
    dueSummary_one:
      "{{count}} template due — the daily job issues it, the button only makes it happen now.",
    dueSummary_few:
      "{{count}} templates due — the daily job issues them, the button only makes it happen now.",
    dueSummary_other:
      "{{count}} templates due — the daily job issues them, the button only makes it happen now.",
    nothingDue: "Nothing due. The daily job issues each template on its own date.",
    queueing: "Queueing…",
    issueDue: "Issue what's due",
    close: "Close",
    newTemplate: "New template",
    runError: "Couldn't queue the run.",
    templates: "Templates",
    templatesDescription:
      "A paused template stops billing but keeps its history — the invoices it already issued stay where they are.",
    empty: "No templates yet. The Fakturoid import brings the existing generators across.",
    col: {
      name: "Name",
      partner: "Partner",
      period: "Period",
      next: "Next",
      amount: "Amount",
      lastInvoice: "Last invoice",
    },
    period: {
      monthly: "monthly",
      quarterly: "quarterly",
      twiceYear: "twice a year",
      yearly: "yearly",
      every_one: "every {{count}} months",
      every_few: "every {{count}} months",
      every_other: "every {{count}} months",
    },
    issueError: "Couldn't issue it.",
    toggleError: "Couldn't change it.",
    paused: "paused",
    dueBadge: "due",
    never: "never",
    issuing: "Issuing…",
    issueNow: "Issue now",
    pause: "pause",
    resume: "resume",
    closeRow: "close",
    edit: "edit",
    saveError: "Couldn't save the template.",
    createError: "Couldn't create the template.",
    editTitle: "Edit template",
    newTitle: "New template",
    editDescription:
      "Changes apply from the next invoice; the ones already issued stay as they are.",
    newDescription:
      "The first invoice goes out on the start date — the schedule starts with it, not after it.",
    field: {
      name: "Name",
      namePlaceholder: "Hosting — monthly",
      contact: "Contact",
      everyMonths: "Every (months)",
      nextInvoiceOn: "Next invoice on",
      firstInvoiceOn: "First invoice on",
      currency: "Currency",
      dueDays: "Due in (days)",
      dueDaysPlaceholder: "From the client",
      description: "Description",
      qty: "Qty",
      unitPrice: "Unit price",
      vatPercent: "VAT %",
    },
    datePast: ", in the past: the invoice would be backdated",
    remove: "remove",
    addLine: "Add a line",
    saving: "Saving…",
    save: "Save",
    create: "Create",
  },
};

export const accVatContactsCs: typeof accVatContactsEn = {
  accVat: {
    title: "DPH",
    subtitle:
      "Plnění spadají do období podle data uskutečnění zdanitelného plnění, ne podle data vystavení.",
    year: "Rok",
    quarter: "Čtvrtletí",
    month: "Měsíc",
    monthly: "Měsíčně",
    quarterly: "Čtvrtletně",
    vatDue: "Daň k úhradě",
    vatRefund: "Nadměrný odpočet",
    balance: "DPH na výstupu {{output}} − DPH na vstupu {{input}}",
    building: "Sestavuji…",
    rebuild: "Sestavit znovu",
    buildReturn: "Sestavit přiznání",
    importing: "Importuji…",
    importFiled: "Importovat podaná přiznání",
    reasonsFoundOn: "Důvody zjištěny dne",
    reasons: "Důvody dodatečného přiznání",
    reasonsHint:
      "Povinné, když dodatečné přiznání daň snižuje (§ 141 daňového řádu): bez nich se přiznání nedá odeslat datovou schránkou. Přikládají se jako textová příloha.",
    withFollowUp: "s následným kontrolním hlášením",
    buildAdditional: "Sestavit dodatečné přiznání",
    buildError: "Přiznání se nepodařilo sestavit.",
    importError: "Podaná přiznání se nepodařilo importovat.",
    markError: "Označení se nepodařilo.",
    footnote:
      "Soubory se nahrávají na daňový portál (EPO). Hub je připraví, podává je člověk. Řídí se zveřejněným XSD portálu. Dodatečné přiznání obsahuje jen rozdíly oproti poslední známé dani; následné kontrolní hlášení je úplné.",
    output: "Výstup",
    input: "Vstup",
    form: {
      regular: "řádné (B)",
      corrective: "opravné (O)",
      additional: "dodatečné (D)",
    },
    mode: {
      standard: "tuzemské",
      reverse_charge: "přenesená daňová povinnost",
      eu_supply: "dodání do EU",
      export: "vývoz",
      exempt: "osvobozeno",
      outside_scope: "mimo předmět daně",
    },
    messageState: {
      sent: "odesláno",
      inBox: "ve schránce úřadu",
      deliveredFiction: "doručeno (fikce)",
      delivered: "doručeno",
      unknown: "stav {{state}}",
    },
    dataMessage: "datová zpráva",
    returns: {
      form: "Druh",
      state: "Stav",
      taxDue: "Daň",
      difference: "Rozdíl",
      files: "Soubory",
      filed: "podáno",
      portal: "(portál)",
      draft: "koncept",
      markFiled: "Označit jako podané",
      sendByDataBox: "Odeslat datovou schránkou: {{label}}",
    },
    yearDocs: {
      title: "{{year}} z dokladů",
      description:
        "Přiznání tak, jak ho dávají doklady, měsíc po měsíci: importované doklady zařazené podle pravidel, cizí měna v korunách kurzem dokladu.",
      computing: "Počítám…",
      month: "Měsíc",
      outputVat: "DPH na výstupu",
      deduction: "Odpočet",
      taxDue: "Daň",
      documents: "Doklady",
      needsPerson: "Vyžaduje člověka",
      toDecide: "{{count}} k rozhodnutí",
    },
    lines: {
      title: "{{month}}/{{year}}: řádky přiznání z dokladů",
      description:
        "Výstup {{output}} · odpočet {{deduction}} · daň {{taxDue}} (celé koruny, po řádcích)",
      lineNo: "ř. {{key}}",
      base: "základ {{value}}",
      vat: "DPH {{value}}",
      vatCzk: "DPH {{value}} CZK",
      notDeductible: "Neuplatnitelné, daň v režimu jednoho správního místa ({{count}})",
      unplaced: "Na žádném řádku, dokud někdo nerozhodne ({{count}})",
      label: {
        "1": "Tuzemská plnění, základní sazba",
        "2": "Tuzemská plnění, snížená sazba",
        "3": "Zboží z jiného členského státu, základní sazba",
        "4": "Zboží z jiného členského státu, snížená sazba",
        "5": "Služby z jiného členského státu (§9 odst. 1), základní sazba",
        "6": "Služby z jiného členského státu, snížená sazba",
        "10": "Přijatá tuzemská přenesená daňová povinnost (§92a), základní sazba",
        "11": "Přijatá tuzemská přenesená daňová povinnost, snížená sazba",
        "12": "Ostatní plnění zdaňovaná příjemcem (§108), základní sazba",
        "13": "Ostatní plnění zdaňovaná příjemcem, snížená sazba",
        "21": "Služby do jiného členského státu",
        "25": "Poskytnutá tuzemská přenesená daňová povinnost (§92a)",
        "26": "Ostatní plnění s nárokem na odpočet",
        "40": "Odpočet, od plátců, základní sazba",
        "41": "Odpočet, od plátců, snížená sazba",
        "43": "Odpočet daně z řádků 3 až 13, základní sazba",
        "44": "Odpočet daně z řádků 3 až 13, snížená sazba",
      },
    },
    table: {
      rate: "Sazba",
      mode: "Režim",
      base: "Základ",
      vat: "DPH",
      nothing: "V tomto období nic není.",
      total: "DPH celkem",
    },
  },
  accContacts: {
    role: {
      customer: "odběratel",
      supplier: "dodavatel",
      both: "odběratel + dodavatel",
    },
    title: "Kontakty",
    subtitle:
      "Všichni, komu fakturujeme nebo kdo fakturuje nám. Zákazník v Hubu fakturuje přes kontakt, ke kterému je připojen — kontakt nese identitu, která se objeví na dokladu.",
    close: "Zavřít",
    newContact: "Nový kontakt",
    all: "Vše",
    searchPlaceholder: "Název, IČO nebo DIČ…",
    empty: "Zatím tu nic není — import z Fakturoidu naplní adresář, nebo kontakt přidejte ručně.",
    col: {
      name: "Název",
      role: "Role",
      registration: "IČO",
      vatNo: "DIČ",
      linkedCustomers: "Propojení zákazníci",
      documents: "Doklady",
    },
    profile: "Profil",
    createError: "Kontakt se nepodařilo vytvořit.",
    newTitle: "Nový kontakt",
    newDescription: "IČO se kontroluje proti adresáři — jedna firma, jeden řádek.",
    ares: {
      hint: "Zadejte IČO nebo začněte psát název, údaje se doplní z ARES.",
      loading: "Hledám v ARES…",
      notFound: "Firma s tímto IČO v ARES není.",
    },
    creating: "Vytvářím…",
    create: "Vytvořit",
    field: {
      name: "Název",
      role: "Role",
      registrationNo: "IČO",
      vatNo: "DIČ",
      email: "E-mail",
      phone: "Telefon",
      street: "Ulice",
      zip: "PSČ",
      city: "Město",
      country: "Země",
      dueDays: "Splatnost (dny)",
      dueDaysPlaceholder: "Výchozí z nastavení",
      currency: "Měna",
      note: "Poznámka",
      active: "Aktivní",
    },
    detail: {
      back: "Kontakty",
      inactive: "neaktivní",
      noAddress: "Adresa není vyplněna",
      edit: "Upravit",
      close: "Zavřít",
      theyOweUs: "Dluží nám ({{count}})",
      weOweThem: "Dlužíme jim ({{count}})",
      overdue: "Z toho po splatnosti ({{count}})",
      identityTitle: "Fakturační identita",
      identityDescription: "Co se objeví na dalším dokladu, který jim vystavíme.",
      dueIn: "Splatnost",
      defaultFromSettings: "Výchozí z nastavení",
      days_one: "{{count}} den",
      days_few: "{{count}} dny",
      days_other: "{{count}} dní",
      customersTitle: "Propojení zákazníci v Hubu",
      customersDescription: "Kdo fakturuje přes tento kontakt.",
      supplierTitle: "Jako dodavatel",
      supplierDescription: "Záznamy dodavatelů svázané s tímto kontaktem.",
      noSupplier: "Žádné — nic, co nám vyfakturovali, nebylo svázáno se záznamem dodavatele.",
      weInvoiced: "Fakturovali jsme jim",
      theyInvoiced: "Fakturovali nám",
      lastTwenty: "Posledních dvacet.",
      allLink: "Vše",
      nothingYet: "Zatím nic.",
      noCustomers: "Žádní — tento kontakt se fakturuje přímo, bez projektové strany.",
      unlink: "odpojit",
      linkCustomer: "Připojit zákazníka…",
      linkError: "Zákazníka se nepodařilo připojit.",
      unlinkError: "Zákazníka se nepodařilo odpojit.",
      saved: "Kontakt uložen",
      saveError: "Kontakt se nepodařilo uložit.",
      editTitle: "Upravit kontakt",
      editDescription:
        "Již vystavené doklady si ponechají identitu, se kterou byly vystaveny — mění se to, co bude na dalším.",
      saving: "Ukládám…",
      save: "Uložit",
    },
    status: {
      paid: "zaplaceno",
      partially_paid: "částečně zaplaceno",
      issued: "vystaveno",
      draft: "koncept",
      cancelled: "zrušeno",
    },
  },
  accRecurring: {
    title: "Opakované faktury",
    dueSummary_one:
      "K vystavení čeká {{count}} šablona — vystaví ji denní úloha, tlačítko jen zařídí, aby se to stalo hned.",
    dueSummary_few:
      "K vystavení čekají {{count}} šablony — vystaví je denní úloha, tlačítko jen zařídí, aby se to stalo hned.",
    dueSummary_other:
      "K vystavení čeká {{count}} šablon — vystaví je denní úloha, tlačítko jen zařídí, aby se to stalo hned.",
    nothingDue: "Nic k vystavení. Denní úloha vystaví každou šablonu v její den.",
    queueing: "Zařazuji…",
    issueDue: "Vystavit, co čeká",
    close: "Zavřít",
    newTemplate: "Nová šablona",
    runError: "Spuštění se nepodařilo zařadit.",
    templates: "Šablony",
    templatesDescription:
      "Pozastavená šablona přestane fakturovat, ale zachová historii — již vystavené faktury zůstávají, kde jsou.",
    empty: "Zatím žádné šablony. Import z Fakturoidu přenese stávající generátory.",
    col: {
      name: "Název",
      partner: "Partner",
      period: "Perioda",
      next: "Další",
      amount: "Částka",
      lastInvoice: "Poslední faktura",
    },
    period: {
      monthly: "měsíčně",
      quarterly: "čtvrtletně",
      twiceYear: "dvakrát ročně",
      yearly: "ročně",
      every_one: "každý {{count}} měsíc",
      every_few: "každé {{count}} měsíce",
      every_other: "každých {{count}} měsíců",
    },
    issueError: "Vystavení se nepodařilo.",
    toggleError: "Změna se nepodařila.",
    paused: "pozastaveno",
    dueBadge: "k vystavení",
    never: "nikdy",
    issuing: "Vystavuji…",
    issueNow: "Vystavit teď",
    pause: "pozastavit",
    resume: "obnovit",
    closeRow: "zavřít",
    edit: "upravit",
    saveError: "Šablonu se nepodařilo uložit.",
    createError: "Šablonu se nepodařilo vytvořit.",
    editTitle: "Upravit šablonu",
    newTitle: "Nová šablona",
    editDescription: "Změny se uplatní od další faktury; již vystavené zůstávají, jak jsou.",
    newDescription: "První faktura odejde v den zahájení — rozvrh jím začíná, ne po něm.",
    field: {
      name: "Název",
      namePlaceholder: "Hosting — měsíčně",
      contact: "Kontakt",
      everyMonths: "Každých (měsíců)",
      nextInvoiceOn: "Další faktura dne",
      firstInvoiceOn: "První faktura dne",
      currency: "Měna",
      dueDays: "Splatnost (dny)",
      dueDaysPlaceholder: "Od klienta",
      description: "Popis",
      qty: "Množ.",
      unitPrice: "Cena za jednotku",
      vatPercent: "DPH %",
    },
    datePast: ", v minulosti: faktura by byla zpětně datovaná",
    remove: "odebrat",
    addLine: "Přidat řádek",
    saving: "Ukládám…",
    save: "Uložit",
    create: "Vytvořit",
  },
};
